'use client';

import { Link } from "@akinon/next/components";
import { useGetWidgetQuery } from "@akinon/next/data/client/misc";
import { useLocalization } from "@akinon/next/hooks";
import { Product } from "@akinon/next/types";
import { Button } from "@theme/components";
import { ROUTES } from "@theme/routes";
import { cn } from "@theme/utils"
// eslint-disable-next-line @akinon/projectzero/image-import
import Image from "next/image";
import { useEffect, useState } from "react";

export const SearchNotFound = ({ searchText }: { searchText: string }) => {
  const { t } = useLocalization();
  const [previousSearchItems, setPreviousSearchItems] = useState([]);

  const {
    data: content
  } = useGetWidgetQuery('pznext-no-results-content');

  const {
    data: trendSearches
  } = useGetWidgetQuery('pznext-trend-searches');

  const {
    data: bestSearches
  } = useGetWidgetQuery('pznext-best-searches');

  useEffect(() => {
    setPreviousSearchItems(
      JSON.parse(localStorage.getItem('prevFashfedSearches')) || []
    );
  }, [searchText]);

  return (
    <>
      <div className={cn('xs:container mx-auto my-10 lg:my-4')}>
        <div className="flex flex-col md:items-center gap-10">
          <div className="flex items-center justify-center">
            {content?.attributes?.image?.kwargs?.url && <Image
              src={content?.attributes?.image?.kwargs?.url}
              alt="Not Found"
              width={246}
              height={246}
              className="hidden md:block"
            />}
            {content?.attributes?.mobile_image?.kwargs?.url && <Image
              src={content?.attributes?.mobile_image?.kwargs?.url}
              alt="Not Found"
              width={246}
              height={246}
              className="md:hidden"
            />}
          </div>
          <div className="flex flex-col items-center gap-6 md:gap-10">
            <div className="text-center text-lg font-semibold leading-[1.44] max-w-[230px] mx-auto">
              {content?.attributes?.title?.value || t('common.search.not_found')}
            </div>
            <div className="hidden md:block text-center leading-[1.5] md:max-w-[500px]">
              {content?.attributes?.description?.value || t('common.search.not_found_description')}
            </div>
            {content?.attributes?.mobile_description?.value && <div className="md:hidden text-center leading-[1.5] max-w-[300px]">
              {content?.attributes?.mobile_description?.value}
            </div>}
            <Link href={content?.attributes?.button_link?.value || ROUTES.HOME} className="w-full px-6 md:px-0 md:w-[328px]">
              <Button className="w-full">
                {content?.attributes?.button_text?.value || t('common.search.back')}
              </Button>
            </Link>
          </div>
          <div className="flex flex-col md:flex-row md:flex-wrap md:gap-20 2xl:gap-[175px]">
            {previousSearchItems?.length > 0 &&
              <div className="flex flex-col pl-6 md:px-0 border-b border-gray-600 md:border-none pb-8 md:pb-0">
                <div className="mb-6 flex items-center justify-between pr-6 md:pr-0">
                  <div className="font-bold text-lg leading-[1.44]">
                    {t('common.search.prev_searches')}
                  </div>
                  <div
                    className="md:hidden cursor-pointer text-sm font-medium underline"
                    onClick={() => {
                      localStorage.removeItem('prevFashfedSearches');
                      setPreviousSearchItems([]);
                    }}
                  >
                    {t('common.search.clean')}
                  </div>
                </div>
                <div className="flex items-center overflow-auto gap-9 md:gap-y-4 md:gap-x-8 md:flex-wrap md:max-w-[282px]">
                  {previousSearchItems.map((search, index) => (
                    <Link
                      key={index}
                      href={`${ROUTES.LIST}/?search_text=${search}`}
                      className="leading-[1.5] whitespace-nowrap"
                    >
                      {search}
                    </Link>
                  ))}
                </div>
              </div>}

            {trendSearches?.attributes &&
              <div className={cn(
                "border-b border-gray-600 md:border-none pb-8 px-6 md:p-0",
                previousSearchItems?.length > 0 && 'pt-8 md:pt-0'
              )}>
                <div className="mb-6 font-bold text-lg leading-[1.44]">
                  {trendSearches?.attributes?.title?.value}
                </div>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 w-full">
                  {trendSearches?.attributes?.searches?.map((trend, index) => (
                    <li key={index} className="text-base leading-[1.5]">
                      <Link href={trend?.value?.link}>{trend?.value?.search}</Link>
                    </li>
                  ))}
                </ul>
              </div>}

            {
              bestSearches?.attributes &&
              <div className="border-b border-gray-600 md:border-none py-8 px-6 md:p-0">
                <div className="mb-6 font-bold text-lg leading-[1.44]">
                  {bestSearches?.attributes?.section_title?.value}
                </div>
                <div className="flex flex-col gap-4">
                  {bestSearches?.products?.map((product: Product, key: number) => (
                    <Link
                      key={key}
                      className={cn(
                        "underline font-medium text-base leading-[1.5] w-full",
                        previousSearchItems?.length < 1 ? 'md:w-[352px]' : 'md:w-[180px]'
                      )}
                      href={product.absolute_url}
                    >{product.name}</Link>
                  ))}
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </>
  )
}